import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";
import "../assets/css/Signin.scss";
import { LoginLogo } from "../assets/icons";
import ButtonLoader from "../atoms/buttonLoader";
import { verifyOTP, forgotPassword } from "../services/service";
import { CustomToast } from "../atoms/toastMessage";
import buildLoginBg from "../assets/images/dashboard/buildloginbg.svg";

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  
  const [OTP, setOTP] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // Redirect if no email is provided
  useEffect(() => {
    if (!email) {
      navigate("/forgotpassword");
    }
  }, [email, navigate]);

  // Timer countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleVerifyOTP = async () => {
    if (OTP.length !== 6) {
      CustomToast({
        type: "error",
        message: "Please enter a valid 6-digit OTP",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await verifyOTP({ email, OTP });

      if (response) {
        CustomToast({
          type: "success",
          message: "OTP verified successfully",
        });
        // Navigate to reset password page with email and token
        navigate("/reset-password", { 
          state: { 
            email, 
            token: response.token || OTP 
          } 
        });
      }
    } catch (error) {
      const errorMessage = error.message || "Invalid OTP. Please try again.";
      CustomToast({
        type: "error",
        message: errorMessage,
      });
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setLoading(true);
    try {
      const response = await forgotPassword({ email });

      if (response) {
        CustomToast({
          type: "success",
          message: "OTP resent successfully",
        });
        setTimer(30);
        setCanResend(false);
        setOTP("");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to resend OTP";
      CustomToast({
        type: "error",
        message: errorMessage,
      });
    }
    setLoading(false);
  };

  return (
    <div className="fluid-container">
      <div className="sign-page" style={{ backgroundImage: `url(${buildLoginBg})` }}>
        <div className="sign-page-background d-flex align-items-center">
          <div
            className="row m-0 sign-page-background-row"
            style={{ justifyContent: "center", alignItems: "center" }}
          >
            <div className="custome_offset px-lg-5 mahzen-logo d-flex justify-content-center flex-column">
              <div style={{ height: "100%", width: "100%" }}>
                <div className="row mx-lg-5 px-lg-5 pt-3 pb-2 siginup-inner py-5">
                  <div className="w-full d-flex justify-content-center">
                    <div className="login-logo d-flex justify-content-center align-items-end">
                      {LoginLogo}
                    </div>
                  </div>
                  <div className="col-12 mb-3 d-flex justify-content-center">
                    <p className="mb-0 text-[#1E3A8A] font-semibold text-[28px]">
                      Verify OTP
                    </p>
                  </div>
                  <div className="col-12 mb-3 d-flex justify-content-center text-center">
                    <p className="mb-0 text-[#64748B] text-[14px]">
                      We've sent a 6-digit OTP to <strong>{email}</strong>
                    </p>
                  </div>

                  <div className="col-12 mt-4 d-flex justify-content-center">
                    <OtpInput
                      value={OTP}
                      onChange={setOTP}
                      numInputs={6}
                      renderSeparator={<span style={{ width: "12px" }}></span>}
                      renderInput={(props) => (
                        <input
                          {...props}
                          style={{
                            width: "50px",
                            height: "54px",
                            fontSize: "20px",
                            fontWeight: "600",
                            textAlign: "center",
                            border: "1px solid #CBD5E1",
                            borderRadius: "8px",
                            outline: "none",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#F97316";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      )}
                      shouldAutoFocus={true}
                      inputType="tel"
                    />
                  </div>

                  <div className="col-12 mt-3 d-flex justify-content-center">
                    <p className="mb-0 text-[14px] text-[#64748B]">
                      {canResend ? (
                        <button
                          onClick={handleResendOTP}
                          disabled={isLoading}
                          className="text-[#F97316] hover:underline bg-transparent border-0 cursor-pointer"
                        >
                          Resend OTP
                        </button>
                      ) : (
                        <>Resend OTP in {timer}s</>
                      )}
                    </p>
                  </div>

                  <div className="w-full flex justify-center items-center">
                    <button
                      onClick={handleVerifyOTP}
                      className="w-[94.5%] bg-[#F97316] h-[52px] rounded-[4px] mt-6 flex items-center justify-center"
                      disabled={isLoading || OTP.length !== 6}
                    >
                      {isLoading ? (
                        <ButtonLoader color="#FFFFFF" />
                      ) : (
                        <p className="font-semibold text-[20px] text-white">
                          Verify OTP
                        </p>
                      )}
                    </button>
                  </div>

                  <div className="col-12 mt-4 mb-2 text-center">
                    <p className="mb-0 text-[14px]">
                      Remember your password?{" "}
                      <Link
                        to="/signin"
                        className="text-[#F97316] hover:underline"
                      >
                        Sign In
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;

